import App from 'common/backbone-app';
import Wahanda from 'common/wahanda';
import _ from 'underscore';

export const FILTER_TYPE_ALL = 'ALL';
export const FILTER_TYPE_CANCELLED = 'BCX';
export const FILTER_TYPE_REJECTED = 'BRJ';
export const FILTER_TYPE_FUTURE = 'BFU';
export const FILTER_TYPE_ALL_APPOINTMENTS = 'AAP';
export const FILTER_TYPE_CUSTMR_APPOINTMENTS = 'AWH';
export const FILTER_TYPE_SUPCUS_APPOINTMENTS = 'AWG';
export const FILTER_TYPE_SUPPLR_APPOINTMENTS = 'ADR';
export const FILTER_TYPE_COMPLETED_APPOINTMENTS = 'ACP';
export const FILTER_TYPE_SPA_DAYS = 'DTY';
export const FILTER_TYPE_SPA_BREAKS = 'DTO';
export const FILTER_TYPE_EVOUCHERS_REDEEMED = 'REV';
export const FILTER_TYPE_EVOUCHERS_ACTIVE = 'AEV';

const BOOKING_SUBTYPE_EVOUCHER = 'EV';
const BOOKING_SUBTYPE_APPOINTMENT = 'AP';
const BOOKING_SUBTYPE_DATED = 'DT';

const BOOKING_STATUS_CONFIRMED = 'CN';
const BOOKING_STATUS_CANCELLED = 'CC';
const BOOKING_STATUS_REJECTED = 'RJ';

const APPOINTMENT_STATUS_CANCELLED = 'CC';
const APPOINTMENT_STATUS_CHECKED_OUT = 'CP';
const APPOINTMENT_STATUS_REJECTED = 'RJ';
const APPOINTMENT_STATUS_NOSHOW = 'NS';

export const CUSTOM_DATE_FILTER_REGEX = /^C_([\d-]+)_([\d-]+)$/;

export type BookingsEnquiriesModel = {
  each: (fn: (item: Record<string, unknown>) => void) => void;
};

export type BookingsReportTableRow = {
  id: number | string;
  type: string;
  subtype: string;
  orderRef: string;
  orderId?: number;
  date: unknown;
  sortDate?: string;
  customer: string;
  service: string;
  value: unknown;
  status: unknown;
  unpaid?: boolean;
  paymentProtectionApplied?: boolean;
  appointmentDatetime?: string;
  employeeName?: string;
  venueCustomerId?: unknown;
  appointmentDate?: unknown;
  confirmedEvouchers?: number;
  activeEvouchers?: number;
  voidedEvouchers?: number;
  expiredEvouchers?: number;
  reservedEvouchers?: number;
};

type BookingsReportAppointmentCore = {
  id: number;
  isInThePast: () => boolean;
  get: (k: string) => unknown;
  getConsumerName: () => string;
};

type BookingsReportAppointmentRowSource = BookingsReportAppointmentCore & {
  isCheckedOut: () => boolean;
  isUnpaid: (o: { onlyPayAtVenue: boolean }) => boolean;
  isPaymentProtectionApplied: () => boolean;
  getStartDate: () => unknown;
};

type BookingsReportStatusBadge =
  | { type: 'status'; className: string; text: string }
  | { type: 'paymentProtection' };

export function getItemIcon(item: BookingsReportTableRow): { className: string; title: string } {
  const type = getItemIconType(item);
  const icons = Wahanda.lang.home.table.icons as Record<string, string>;
  return {
    className: type + (type === 'voucher' ? '3' : ''),
    title: icons[type] ?? '',
  };
}

function getItemIconType(item: BookingsReportTableRow): string {
  if (item.type === 'order') {
    if (item.subtype === BOOKING_SUBTYPE_APPOINTMENT) {
      return 'appointment';
    }
    if (item.subtype === BOOKING_SUBTYPE_DATED) {
      return 'dated';
    }
    return 'voucher';
  }
  if (item.type === 'appointment') {
    return 'appointment';
  }
  return 'unknown';
}

export function getItemStatus(
  item: BookingsReportTableRow,
): { badges: BookingsReportStatusBadge[] } {
  if (item.type === 'order' || item.type === 'appointment') {
    switch (item.subtype) {
      case BOOKING_SUBTYPE_EVOUCHER: {
        const badges: BookingsReportStatusBadge[] = [];
        const checks: Record<string, { textId: string; className: string }> = {
          confirmedEvouchers: { textId: 'confirmedCount', className: 'done' },
          activeEvouchers: { textId: 'activeCount', className: 'confirmed' },
          voidedEvouchers: { textId: 'voidedCount', className: 'done' },
          expiredEvouchers: { textId: 'expiredCount', className: 'done' },
          reservedEvouchers: { textId: 'reservedCount', className: 'done' },
        };
        Object.keys(checks).forEach((name) => {
          const n = ((item as unknown) as Record<string, number>)[name];
          if (n > 0) {
            const data = checks[name];
            const text = Wahanda.Template.render(
              Wahanda.lang.home.table.evouchers[
                data.textId as keyof typeof Wahanda.lang.home.table.evouchers
              ],
              { count: n },
            );
            badges.push({ type: 'status', className: data.className, text });
          }
        });
        return { badges };
      }
      case BOOKING_SUBTYPE_APPOINTMENT: {
        const badges: BookingsReportStatusBadge[] = [];
        if (APPOINTMENT_STATUS_REJECTED === item.status) {
          badges.push({
            type: 'status',
            className: 'rejected',
            text: Wahanda.lang.home.table.statuses.rejected,
          });
        } else if (APPOINTMENT_STATUS_CHECKED_OUT === item.status) {
          badges.push({
            type: 'status',
            className: 'checkedout',
            text: Wahanda.lang.home.table.statuses.checkedOut,
          });
        } else if (BOOKING_STATUS_CONFIRMED === item.status) {
          badges.push({
            type: 'status',
            className: 'confirmed',
            text: Wahanda.lang.home.table.statuses.confirmed,
          });
        } else if (APPOINTMENT_STATUS_CANCELLED === item.status) {
          badges.push({
            type: 'status',
            className: 'cancelled',
            text: Wahanda.lang.home.table.statuses.cancelled,
          });
        } else if (APPOINTMENT_STATUS_NOSHOW === item.status) {
          badges.push({
            type: 'status',
            className: 'noshow',
            text: Wahanda.lang.calendar.appointments.types.noShow,
          });
        }
        if (item.paymentProtectionApplied) {
          badges.push({ type: 'paymentProtection' });
        }
        if (item.unpaid) {
          badges.push({
            type: 'status',
            className: 'unpaid',
            text: Wahanda.lang.calendar.appointments.multi.labels.unpaid,
          });
        }
        return { badges };
      }
      case BOOKING_SUBTYPE_DATED: {
        const badges: BookingsReportStatusBadge[] = [];
        if (BOOKING_STATUS_CONFIRMED === item.status) {
          badges.push({
            type: 'status',
            className: 'confirmed',
            text: Wahanda.lang.home.table.statuses.confirmed,
          });
        } else if (BOOKING_STATUS_CANCELLED === item.status) {
          badges.push({
            type: 'status',
            className: 'cancelled',
            text: Wahanda.lang.home.table.statuses.cancelled,
          });
        } else if (BOOKING_STATUS_REJECTED === item.status) {
          badges.push({
            type: 'status',
            className: 'rejected',
            text: Wahanda.lang.home.table.statuses.rejected,
          });
        }
        if (item.unpaid) {
          badges.push({
            type: 'status',
            className: 'unpaid',
            text: Wahanda.lang.calendar.appointments.multi.labels.unpaid,
          });
        }
        return { badges };
      }
      default:
        break;
    }
  }
  return { badges: [] };
}

export function getEntityFilter(filterType: string): string {
  const appointmentType = [
    FILTER_TYPE_ALL_APPOINTMENTS,
    FILTER_TYPE_CUSTMR_APPOINTMENTS,
    FILTER_TYPE_SUPCUS_APPOINTMENTS,
    FILTER_TYPE_SUPPLR_APPOINTMENTS,
    FILTER_TYPE_COMPLETED_APPOINTMENTS,
  ];
  const datedType = [FILTER_TYPE_SPA_DAYS, FILTER_TYPE_SPA_BREAKS];
  const evoucherType = [FILTER_TYPE_EVOUCHERS_REDEEMED, FILTER_TYPE_EVOUCHERS_ACTIVE];

  if (_.indexOf(appointmentType, filterType) !== -1) {
    return 'appointment-booking';
  }
  if (_.indexOf(datedType, filterType) !== -1) {
    return 'dated-booking';
  }
  if (_.indexOf(evoucherType, filterType) !== -1) {
    return 'evoucher-booking';
  }
  return 'all';
}

export function getTypeFilter(filterType: string): string {
  switch (filterType) {
    case FILTER_TYPE_CANCELLED:
      return 'cancelled';
    case FILTER_TYPE_REJECTED:
      return 'rejected';
    case FILTER_TYPE_CUSTMR_APPOINTMENTS:
      return 'channel-site';
    case FILTER_TYPE_SUPCUS_APPOINTMENTS:
      return 'channel-supcus';
    case FILTER_TYPE_SUPPLR_APPOINTMENTS:
      return 'channel-supplier';
    case FILTER_TYPE_COMPLETED_APPOINTMENTS:
      return 'status-completed';
    case FILTER_TYPE_SPA_DAYS:
      return 'spa-day';
    case FILTER_TYPE_SPA_BREAKS:
      return 'spa-break';
    case FILTER_TYPE_EVOUCHERS_REDEEMED:
      return 'redeemed-evoucher';
    case FILTER_TYPE_EVOUCHERS_ACTIVE:
      return 'active-evoucher';
    case FILTER_TYPE_FUTURE:
      return 'future';
    default:
      return 'all';
  }
}

export function getCustomDateRange(filterDate: string): { from: Date; to: Date } | null {
  const match = String(filterDate).match(CUSTOM_DATE_FILTER_REGEX);
  if (!match) {
    return null;
  }
  return {
    from: Wahanda.Date.createDate(match[1]),
    to: Wahanda.Date.createDate(match[2]),
  };
}

export function getRange(filters: {
  type: string;
  date: string;
}): { from: Date | null; to: Date | null } {
  let from: Date | null = null;
  let until: Date | null = new Date();

  if (filters.type === FILTER_TYPE_FUTURE) {
    from = until;
    until = null;
  } else if (CUSTOM_DATE_FILTER_REGEX.test(filters.date)) {
    const match = getCustomDateRange(filters.date);
    if (match) {
      from = match.from;
      until = match.to;
    }
  } else {
    switch (filters.date) {
      case '1M':
        from = new Date(until.getFullYear(), until.getMonth(), until.getDate() - 30);
        break;
      case '2M':
        from = new Date(until.getFullYear(), until.getMonth(), until.getDate() - 60);
        break;
      case '3M':
        from = new Date(until.getFullYear(), until.getMonth() - 3, until.getDate());
        break;
      case '6M':
        from = new Date(until.getFullYear(), until.getMonth() - 6, until.getDate());
        break;
      case '12M':
        from = new Date(until.getFullYear() - 1, until.getMonth(), until.getDate());
        break;
      default:
        from = new Date(until.getFullYear(), until.getMonth() - 3, until.getDate());
        break;
    }
  }

  return { from, to: until };
}

export function getFilterDates(filters: {
  type: string;
  date: string;
}): {
  from: string;
  to: string | null;
} {
  const range = getRange(filters);
  return {
    from: Wahanda.Date.toApiString(range.from as Date),
    to: range.to ? Wahanda.Date.toApiString(range.to) : null,
  };
}

function isValidItemForRendering(item: Record<string, unknown>): boolean {
  if (App.Models.BookingsEnquiries.itemBelongsToGroup(item)) {
    return false;
  }
  return true;
}

function getItemSubtype(booking: { get: (k: string) => unknown }): string {
  if (booking.get('appointment')) {
    return BOOKING_SUBTYPE_APPOINTMENT;
  }
  if (booking.get('dated')) {
    return BOOKING_SUBTYPE_DATED;
  }
  return BOOKING_SUBTYPE_EVOUCHER;
}

function getAppointmentDatetime(appointment: { getStartDate: () => unknown }): string {
  const start = appointment.getStartDate();
  const startInfo = Wahanda.Date.formatToDefaultFullDate(start);
  return `${startInfo.date} ${startInfo.time}`;
}

function extractBookingData(
  filterType: string,
  booking: {
    get: (k: string) => unknown;
    isAppointment: () => boolean;
    getAppointmentModel: () => unknown;
    isUnpaid: (o: { onlyPayAtVenue: boolean }) => boolean;
    isPaymentProtectionApplied: () => boolean;
  },
): BookingsReportTableRow | undefined {
  const isEvoucher = !!(
    booking.get('evouchers') && (booking.get('evouchers') as unknown[]).length > 0
  );

  const appointment = booking.isAppointment() && booking.getAppointmentModel();

  const item: BookingsReportTableRow = {
    id: booking.get('orderId') as number,
    type: 'order',
    subtype: getItemSubtype(booking),
    orderRef: booking.get('orderReference') as string,
    date: booking.get('created'),
    sortDate:
      filterType === FILTER_TYPE_FUTURE && appointment
        ? ((appointment as { get: (k: string) => unknown }).get('appointmentDate') as string)
        : (booking.get('created') as string),
    customer: (booking.get('customer') as { name: string }).name,
    service: booking.get('itemName') as string,
    value: booking.get('itemAmount'),
    status: booking.get('bookingStatus'),
    unpaid: booking.isUnpaid({ onlyPayAtVenue: true }),
    paymentProtectionApplied: booking.isPaymentProtectionApplied(),
    appointmentDatetime: appointment
      ? getAppointmentDatetime(appointment as { getStartDate: () => unknown })
      : undefined,
    employeeName: appointment
      ? ((appointment as { get: (k: string) => unknown }).get('employeeName') as string | undefined)
      : undefined,
    venueCustomerId: appointment
      ? ((appointment as { get: (k: string) => unknown }).get('venueCustomerId') as
          | string
          | number
          | undefined)
      : undefined,
    orderId: booking.get('orderId') as number,
    appointmentDate: appointment
      ? ((appointment as { get: (k: string) => unknown }).get('appointmentDate') as
          | string
          | undefined)
      : undefined,
  };

  if (isEvoucher) {
    item.confirmedEvouchers = 0;
    item.activeEvouchers = 0;
    item.voidedEvouchers = 0;
    item.expiredEvouchers = 0;
    item.reservedEvouchers = 0;
    _.each(booking.get('evouchers') as Array<{ evoucherStatusCode: string }>, (ev) => {
      switch (ev.evoucherStatusCode) {
        case 'R':
          item.confirmedEvouchers!++;
          break;
        case 'A':
          item.activeEvouchers!++;
          break;
        case 'V':
          item.voidedEvouchers!++;
          break;
        case 'X':
          item.expiredEvouchers!++;
          break;
        case 'S':
          item.reservedEvouchers!++;
          break;
        default:
          break;
      }
    });
  } else if (appointment) {
    const appt = appointment as BookingsReportAppointmentCore;
    if (filterType === FILTER_TYPE_FUTURE && appt.isInThePast()) {
      return undefined;
    }

    item.id = appt.id;
    item.type = 'appointment';
    item.status = appt.get('appointmentStatusCode');
    item.customer = appt.getConsumerName();

    const coData = appt.get('checkout');
    if (APPOINTMENT_STATUS_CHECKED_OUT === item.status && coData) {
      item.value = (coData as { total: unknown }).total;
    } else {
      item.value = appt.get('amount');
    }
  }
  return item;
}

function extractAppointmentData(
  filterType: string,
  appt: BookingsReportAppointmentRowSource,
): BookingsReportTableRow | undefined {
  if (filterType === FILTER_TYPE_FUTURE && appt.isInThePast()) {
    return undefined;
  }

  return {
    id: appt.id,
    orderId: appt.get('orderId') as number,
    type: 'appointment',
    subtype: BOOKING_SUBTYPE_APPOINTMENT,
    orderRef: (appt.get('orderReference') as string) || '',
    date: appt.get('created'),
    sortDate:
      filterType === FILTER_TYPE_FUTURE
        ? (appt.get('appointmentDate') as string)
        : (appt.get('created') as string),
    customer: appt.getConsumerName(),
    service: appt.get('offerName') as string,
    value:
      appt.isCheckedOut() && appt.get('checkout')
        ? (appt.get('checkout') as { total: unknown }).total
        : null,
    status: appt.get('appointmentStatusCode'),
    unpaid: appt.isUnpaid({ onlyPayAtVenue: true }),
    paymentProtectionApplied: appt.isPaymentProtectionApplied(),
    appointmentDatetime: getAppointmentDatetime(appt),
    employeeName: appt.get('employeeName') as string,
    venueCustomerId: appt.get('venueCustomerId'),
    appointmentDate: appt.get('appointmentDate'),
  };
}

function extractAppointmentGroupData(
  filterType: string,
  apptGroup: {
    at: (i: number) => unknown;
    getOfferName: () => string;
    data: { appointmentStatusCode: string; orderId: number };
    isCheckedOut: () => boolean;
    getTotalPrice: () => unknown;
  },
): BookingsReportTableRow | undefined {
  const data = extractAppointmentData(
    filterType,
    apptGroup.at(0) as Parameters<typeof extractAppointmentData>[1],
  );
  if (!data) {
    return undefined;
  }
  data.service = apptGroup.getOfferName();
  data.status = apptGroup.data.appointmentStatusCode;
  data.value = apptGroup.isCheckedOut() ? apptGroup.getTotalPrice() : null;
  (data as { offerId?: number }).offerId = apptGroup.data.orderId;
  return data;
}

export function mergeBookingsEnquiriesData(
  model: BookingsEnquiriesModel,
  filterType: string,
): BookingsReportTableRow[] {
  const data: BookingsReportTableRow[] = [];
  const seenAppointments = new Set<number>();

  model.each((item: Record<string, unknown>) => {
    if (!isValidItemForRendering(item)) {
      return;
    }

    const { booking, appointment, appointmentGroup } = item;
    const bookingModel = booking as { get: (k: string) => unknown } | undefined;
    const apptToCheck =
      appointment ||
      (bookingModel && (bookingModel as { get: (k: string) => unknown }).get('appointment'));

    if (apptToCheck && seenAppointments.has((apptToCheck as { id: number }).id)) {
      return;
    }
    if (apptToCheck) {
      seenAppointments.add((apptToCheck as { id: number }).id);
    }

    if (booking) {
      const row = extractBookingData(
        filterType,
        booking as Parameters<typeof extractBookingData>[1],
      );
      if (row) {
        data.push(row);
      }
    } else if (appointment) {
      const apptData = extractAppointmentData(
        filterType,
        appointment as Parameters<typeof extractAppointmentData>[1],
      );
      if (apptData) {
        data.push(apptData);
      }
    } else if (appointmentGroup) {
      const apptGroupData = extractAppointmentGroupData(
        filterType,
        appointmentGroup as Parameters<typeof extractAppointmentGroupData>[1],
      );
      if (apptGroupData) {
        data.push(apptGroupData);
      }
    }
  });

  const sorted = _.sortBy(data, (r) => r.sortDate ?? r.date);
  return (filterType === FILTER_TYPE_FUTURE
    ? sorted
    : sorted.reverse()) as BookingsReportTableRow[];
}
